import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-logo">
            <div className="w-8 h-8 bg-[#D94E18] rounded-md flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              kaya.ai
            </span>
          </Link>
          <Link to="/" data-testid="privacy-back-btn" className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <h1 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-zinc-400 mb-10">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <div className="prose-custom space-y-8">
          <section>
            <h2>1. Introduction</h2>
            <p>
              kaya.ai ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains what
              information we collect, how we use it, and what rights you have in relation to it. By using the kaya.ai
              service (the "Service"), you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <h3>2.1 Account Information</h3>
            <p>
              When you create an account, we collect your name, email address, and an encrypted version of your password.
              We do not store passwords in plain text.
            </p>

            <h3>2.2 Financial Data</h3>
            <p>
              When you upload bank statements or financial documents, we process the transaction data contained within them
              (dates, amounts, descriptions, account numbers). This data is processed solely for the purpose of providing
              the Service to you.
            </p>

            <h3>2.3 Usage Data</h3>
            <p>
              We may collect information about how you access and use the Service, including your IP address, browser type,
              pages visited, and timestamps. This data is used to improve the Service and ensure security.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, operate, and maintain the Service.</li>
              <li>Process your uploaded financial documents and generate accounting entries.</li>
              <li>Improve the accuracy of our AI classification models (using anonymized, aggregated data only).</li>
              <li>Communicate with you about your account or changes to the Service.</li>
              <li>Detect, prevent, and address security issues or fraudulent activity.</li>
              <li>Comply with applicable legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2>4. Data Sharing & Third Parties</h2>
            <p>
              <strong>We do not sell, trade, or rent your personal or financial data to third parties.</strong> We may share
              data only in the following limited circumstances:
            </p>
            <ul>
              <li><strong>AI Processing:</strong> Transaction descriptions may be sent to third-party AI providers (e.g., Google Gemini) for classification purposes. This data is sent in a de-identified manner and is not stored by the AI provider beyond the processing request.</li>
              <li><strong>Infrastructure:</strong> We use third-party cloud hosting providers to store and process your data. These providers are contractually bound to maintain the security and confidentiality of your data.</li>
              <li><strong>Legal Compliance:</strong> We may disclose your information if required to do so by law, regulation, or legal process.</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Retention</h2>
            <p>
              We retain your account and financial data for as long as your account is active or as needed to provide the
              Service. If you request account deletion, we will delete your data within 30 days, except where retention is
              required by law (e.g., for tax or regulatory purposes). Anonymized, aggregated data may be retained indefinitely
              for analytical purposes.
            </p>
          </section>

          <section>
            <h2>6. Data Security</h2>
            <p>
              We implement industry-standard technical and organizational measures to protect your data, including:
            </p>
            <ul>
              <li>Encryption of data in transit (TLS/SSL) and at rest.</li>
              <li>Secure password hashing (bcrypt).</li>
              <li>Access controls and authentication for all system components.</li>
              <li>Regular security reviews and monitoring.</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or method of electronic storage is 100% secure.
              While we strive to protect your data, <strong>we cannot guarantee its absolute security</strong> and accept
              no liability for unauthorized access that occurs despite our reasonable security measures.
            </p>
          </section>

          <section>
            <h2>7. Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the data we hold about you.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data.</li>
              <li><strong>Erasure:</strong> Request deletion of your data (subject to legal retention requirements).</li>
              <li><strong>Data Portability:</strong> Request your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to certain types of processing.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at <span className="text-zinc-700 font-medium">privacy@kaya.ai</span>.
            </p>
          </section>

          <section>
            <h2>8. Cookies</h2>
            <p>
              kaya.ai uses essential cookies and local storage tokens (JWT) for authentication purposes only. We do not use
              third-party tracking cookies, advertising cookies, or analytics pixels. No cookie consent banner is required
              as we only use strictly necessary cookies.
            </p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>
              The Service is not intended for individuals under the age of 18. We do not knowingly collect personal
              information from children. If we learn that we have collected data from a child, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2>10. International Data Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries other than your country of residence. We ensure
              that appropriate safeguards are in place for such transfers, in compliance with applicable data protection
              laws including the Swiss Federal Act on Data Protection (FADP) and, where applicable, the EU General Data
              Protection Regulation (GDPR).
            </p>
          </section>

          <section>
            <h2>11. Disclaimer of Liability for Financial Data</h2>
            <p>
              <strong>KAYA.AI IS NOT RESPONSIBLE FOR THE ACCURACY, COMPLETENESS, OR INTEGRITY OF ANY FINANCIAL DATA
              PROCESSED THROUGH THE SERVICE.</strong> The AI-generated outputs, including transaction classifications,
              tax calculations, journal entries, and financial reports, are automated suggestions and may contain errors.
              You are solely responsible for verifying all outputs before relying on them for any financial, tax, or
              business purpose.
            </p>
          </section>

          <section>
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting
              the updated policy on this page with a new "Last updated" date. Your continued use of the Service after any
              changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>13. Contact</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us at{' '}
              <span className="text-zinc-700 font-medium">privacy@kaya.ai</span>.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-zinc-400">&copy; {new Date().getFullYear()} kaya.ai. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/terms" className="text-xs text-zinc-500 hover:text-zinc-700">Terms & Conditions</Link>
            <Link to="/privacy" className="text-xs text-zinc-500 hover:text-zinc-700">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
